import { useCallback, useMemo } from "react";
import { Table, Typography, Empty } from "antd";
import type { ColumnsType, TableProps } from "antd/es/table";
import type { SorterResult } from "antd/es/table/interface";
import { useSlicerVariables, useWidgetData } from "../Contexts/dashboardHooks";
import { useDashboardActions } from "../Contexts/dashboardHooks";
import { TABLE_CATEGORY_COL_MAX_WIDTH_PX, TABLE_CATEGORY_NAME_MIN_WIDTH_PX, NAME_GROUP_BYS, categorySortKey } from "../Utils/tableStyles";
import type { TableComponentSpec, TableSortConfig } from "../Processing/types";
import { WidgetInfoButton } from "../Info/WidgetInfoButton";
import { resolveTemplate } from "../Utils/resolveTemplate";

const { Text } = Typography;

type Row = Record<string, unknown>;

/**
 * Keeps the average row pinned to the top regardless of sort direction.
 * AntD negates the comparator result for descending sorts, so the sentinel's
 * sign must flip with direction for the avg row to stay on top either way.
 * Returns null when neither row is the average row.
 */
function avgRowCompare(a: Row, b: Row, direction: "asc" | "desc" | undefined): number | null {
  const aAvg = a.key === "avg";
  const bAvg = b.key === "avg";
  if (!aAvg && !bAvg) return null;
  const top = direction === "desc" ? 1 : -1;
  return aAvg ? top : -top;
}

export function TableWidget({ id }: { id: string }) {
  const data = useWidgetData(id);
  const { patchWidgetDisplaySpec } = useDashboardActions();
  const slicerValues = useSlicerVariables();

  const sortConfig = useMemo<TableSortConfig | undefined>(() => {
    if (!data || data.type !== "table") return undefined;
    return (data.spec as TableComponentSpec).mutations.sortConfig;
  }, [data]);

  const separateByPosition = useMemo(() => {
    if (!data || data.type !== "table") return false;
    return !!(data.spec as TableComponentSpec).mutations.separateByPosition;
  }, [data]);

  const columns = useMemo<ColumnsType<Row> | null>(() => {
    if (!data || data.type !== "table") return null;
    const { columns: rawCols, rows, groupBy } = data.table;
    const isNameGroupBy = NAME_GROUP_BYS.has(groupBy ?? "");

    const hasAnyColor = rows.some((r) =>
      Object.keys(r).some((k) => k.startsWith("__color_")),
    );

    return rawCols.map((col) => {
      const isCategory = col.key === "category";
      const colorKey = `__color_${col.dataIndex}`;
      const labelKey = `__label_${col.dataIndex}`;

      const render = isCategory
        ? (value: unknown, record: Row) => {
            if (record.__is_position_separator) {
              return <span style={{ fontWeight: 700 }}>{String(value ?? "")}</span>;
            }
            return String(value ?? "");
          }
        : (value: unknown, record: Row) => {
            // The label carries the metric's decimal count; the value stays numeric for sorting.
            const label = record[labelKey];
            const text = typeof label === "string" ? label : String(value);
            if (record.__is_position_separator) {
              return value != null ? (
                <span style={{ color: "rgba(0,0,0,0.45)" }}>{text}</span>
              ) : null;
            }
            if (value == null) return null;
            const hex = hasAnyColor ? (record[colorKey] as string | undefined) : undefined;
            return hex ? (
              <span style={{ color: hex, fontWeight: 600 }}>{text}</span>
            ) : (
              text
            );
          };

      const onCell = isCategory
        ? () => ({
            style: {
              maxWidth: TABLE_CATEGORY_COL_MAX_WIDTH_PX,
              ...(isNameGroupBy && { minWidth: TABLE_CATEGORY_NAME_MIN_WIDTH_PX }),
            },
          })
        : undefined;

      const sorter = separateByPosition
        ? { compare: () => 0 as number }
        : isCategory
        ? {
            compare: (a: Row, b: Row) => {
              const avg = avgRowCompare(a, b, sortConfig?.direction);
              if (avg !== null) return avg;
              return categorySortKey(a, groupBy).localeCompare(categorySortKey(b, groupBy));
            },
          }
        : {
            compare: (a: Row, b: Row) => {
              const avg = avgRowCompare(a, b, sortConfig?.direction);
              if (avg !== null) return avg;
              return (Number(a[col.dataIndex]) || 0) - (Number(b[col.dataIndex]) || 0);
            },
          };

      const isActiveSortCol = sortConfig?.columnKey === col.key;
      const sortOrder = isActiveSortCol
        ? (sortConfig!.direction === "asc" ? "ascend" : "descend")
        : undefined;

      return {
        ...col,
        ...(isCategory && {
          width: undefined,
          ellipsis: !isNameGroupBy,
        }),
        onCell,
        sorter,
        sortOrder,
        render,
      };
    });
  }, [data, sortConfig, separateByPosition]);

  const handleSortChange = useCallback<NonNullable<TableProps<Row>["onChange"]>>(
    (_pagination, _filters, sorter) => {
      if (!data || data.type !== "table") return;
      const spec = data.spec as TableComponentSpec;

      const result = Array.isArray(sorter) ? sorter[0] : sorter;
      const newSortConfig: TableSortConfig | undefined =
        result?.order && result.columnKey != null
          ? {
              columnKey: String(result.columnKey),
              direction: result.order === "ascend" ? "asc" : "desc",
            }
          : undefined;

      const currentConfig = spec.mutations.sortConfig;
      if (
        newSortConfig?.columnKey === currentConfig?.columnKey &&
        newSortConfig?.direction === currentConfig?.direction
      ) return;

      const updatedSpec: TableComponentSpec = {
        ...spec,
        mutations: { ...spec.mutations, sortConfig: newSortConfig },
      };
      patchWidgetDisplaySpec(id, updatedSpec);
    },
    [data, id, patchWidgetDisplaySpec],
  );

  const rowClassName = useCallback(
    (record: Row) => {
      if (record.__is_position_separator) return "pos-separator-row";
      if (record.key === "avg") return "avg-row";
      return "";
    },
    [],
  );

  if (!data || data.type !== "table") {
    return (
      <div className="table-widget" data-grid-id={id} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No table data" />
      </div>
    );
  }

  const { rows } = data.table;
  const spec = data.spec as TableComponentSpec;
  const title = resolveTemplate(spec.display.title ?? "", slicerValues).resolved
    || spec.series[0]?.displayName
    || "Table";

  return (
    <div className="table-widget" data-grid-id={id}>
      <div className="widget-header">
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Text strong style={{ fontSize: 18 }}>{title}</Text>
            <WidgetInfoButton id={id} />
          </span>
        </div>
      <Table
        columns={columns ?? []}
        dataSource={rows}
        pagination={false}
        size="small"
        bordered={false}
        style={{ width: "100%" }}
        scroll={{ y: "100%" }}
        rowClassName={rowClassName}
        showSorterTooltip={false}
        onChange={handleSortChange}
      />
    </div>
  );
}
